import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, CalendarIcon, X, Search, MoreHorizontal, DollarSign, XCircle, Mail, FileText, Check } from 'lucide-react';
import api from '../utils/api';
import { formatCurrency, formatDate, getStatusLabel, getStatusColor } from '../utils/helpers';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useConfirm } from '../context/ConfirmContext';

export const InvoicesPage = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [invoices, setInvoices] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [customers, setCustomers] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOpen, setCustomerOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [datePreset, setDatePreset] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
    loadInvoices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomers = async () => {
    try {
      const response = await api.getCustomers();
      setAllCustomers(response.data);
      const customersMap = {};
      response.data.forEach(c => {
        customersMap[c.id] = c;
      });
      setCustomers(customersMap);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await api.getInvoices({});
      setInvoices(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento fatture');
    } finally {
      setLoading(false);
    }
  };

  // Apply date preset
  const applyDatePreset = (preset) => {
    const now = new Date();
    let from = null;
    
    switch (preset) {
      case '7d':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1m':
        from = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case '3m':
        from = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case '6m':
        from = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case '12m':
        from = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        from = null;
    }
    
    setDatePreset(preset);
    setDateFrom(from);
    setDateTo(preset ? now : null);
  };

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('');
    setCustomerFilter('');
    setCustomerSearch('');
    setDateFrom(null);
    setDateTo(null);
    setDatePreset('');
  };

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      // Status filter
      if (statusFilter && inv.status !== statusFilter) return false;
      
      // Customer filter
      if (customerFilter && inv.customer_id !== customerFilter) return false;
      
      // Date filter
      if (dateFrom || dateTo) {
        const invDate = new Date(inv.created_at);
        if (dateFrom && invDate < dateFrom) return false;
        if (dateTo) {
          const endOfDay = new Date(dateTo);
          endOfDay.setHours(23, 59, 59, 999);
          if (invDate > endOfDay) return false;
        }
      }
      
      return true;
    });
  }, [invoices, statusFilter, customerFilter, dateFrom, dateTo]);

  // Filtered customers for search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return allCustomers;
    return allCustomers.filter(c => 
      c.name_or_company.toLowerCase().includes(customerSearch.toLowerCase())
    );
  }, [allCustomers, customerSearch]);

  // Check if any filter is active
  const hasActiveFilters = statusFilter || customerFilter || dateFrom || dateTo;

  // Quick actions
  const handleQuickAction = async (invoiceId, action) => {
    setActionLoading(invoiceId);
    try {
      switch (action) {
        case 'settle':
          await api.settleInvoice(invoiceId);
          toast.success('Fattura saldata');
          break;
        case 'cancel': {
          const ok = await confirm({
            title: 'Annulla fattura',
            description: 'La fattura sarà marcata come annullata e non potrà essere usata per la contabilità.',
            confirmText: 'Annulla fattura',
            destructive: true,
          });
          if (!ok) {
            setActionLoading(null);
            return;
          }
          await api.cancelInvoice(invoiceId);
          toast.success('Fattura annullata');
          break;
        }
        default:
          break;
      }
      loadInvoices();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore';
      toast.error(message);
    } finally {
      setActionLoading(null);
    }
  };

  // Get available actions for invoice status
  const getAvailableActions = (status) => {
    switch (status) {
      case 'ISSUED':
      case 'OVERDUE':
        return ['settle', 'cancel'];
      default:
        return [];
    }
  };

  // Get empty message based on filters
  const getEmptyMessage = () => {
    if (customerFilter && statusFilter) {
      return `Nessuna fattura "${getStatusLabel(statusFilter)}" per ${customers[customerFilter]?.name_or_company}`;
    }
    if (customerFilter) {
      return `Nessuna fattura per ${customers[customerFilter]?.name_or_company}`;
    }
    if (statusFilter) {
      return `Nessuna fattura con stato "${getStatusLabel(statusFilter)}"`;
    }
    if (dateFrom || dateTo) {
      return 'Nessuna fattura nel periodo selezionato';
    }
    return 'Nessuna fattura trovata';
  };

  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Fatture</h1>
          <p className="text-slate-600">Gestisci fatture e pagamenti</p>
        </div>
        <Button onClick={() => navigate('/app/invoices/generate')} data-testid="generate-invoice-btn">
          <Plus size={18} className="mr-2" />
          Genera Fattura
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Status Filter */}
            <div className="w-48">
              <Label className="text-xs text-slate-500 mb-1 block">Stato</Label>
              <Select value={statusFilter || '__all__'} onValueChange={(val) => setStatusFilter(val === '__all__' ? '' : val)}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Tutti gli stati" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti gli stati</SelectItem>
                  <SelectItem value="DRAFT">Bozza</SelectItem>
                  <SelectItem value="ISSUED">Emessa</SelectItem>
                  <SelectItem value="OVERDUE">Scaduta</SelectItem>
                  <SelectItem value="SETTLED">Saldata</SelectItem>
                  <SelectItem value="CANCELLED">Annullata</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer Filter with Search */}
            <div className="w-64">
              <Label className="text-xs text-slate-500 mb-1 block">Cliente</Label>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="customer-filter"
                  >
                    {customerFilter ? customers[customerFilter] : "Tutti i clienti"}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Cerca cliente..." 
                      value={customerSearch}
                      onValueChange={setCustomerSearch}
                    />
                    <CommandList>
                      <CommandEmpty>Nessun cliente trovato</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__all__"
                          onSelect={() => {
                            setCustomerFilter('');
                            setCustomerSearch('');
                            setCustomerOpen(false);
                          }}
                        >
                          Tutti i clienti
                        </CommandItem>
                        {filteredCustomers.map(customer => (
                          <CommandItem
                            key={customer.id}
                            value={customer.name_or_company}
                            onSelect={() => {
                              setCustomerFilter(customer.id);
                              setCustomerSearch('');
                              setCustomerOpen(false);
                            }}
                          >
                            {customer.name_or_company}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date From */}
            <div className="w-40">
              <Label className="text-xs text-slate-500 mb-1 block">Data da</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, 'dd.MM.yyyy') : 'Seleziona'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={(date) => {
                      setDateFrom(date);
                      setDatePreset('');
                    }}
                    locale={it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date To */}
            <div className="w-40">
              <Label className="text-xs text-slate-500 mb-1 block">Data a</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, 'dd.MM.yyyy') : 'Seleziona'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={(date) => {
                      setDateTo(date);
                      setDatePreset('');
                    }}
                    locale={it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Presets */}
            <div className="w-36">
              <Label className="text-xs text-slate-500 mb-1 block">Periodo</Label>
              <Select value={datePreset || '__none__'} onValueChange={(val) => applyDatePreset(val === '__none__' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-</SelectItem>
                  <SelectItem value="7d">Ultimi 7 giorni</SelectItem>
                  <SelectItem value="1m">Ultimo mese</SelectItem>
                  <SelectItem value="3m">Ultimi 3 mesi</SelectItem>
                  <SelectItem value="6m">Ultimi 6 mesi</SelectItem>
                  <SelectItem value="12m">Ultimo anno</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700"
              >
                <X size={16} className="mr-1" />
                Azzera filtri
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              Fatture
              {filteredInvoices.length !== invoices.length && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  ({filteredInvoices.length} di {invoices.length})
                </span>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-slate-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <Plus size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-2">{getEmptyMessage()}</p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2">
                  Azzera filtri
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Numero</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Cliente</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Stato</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Data</th>
                    <th className="text-right p-4 text-sm font-semibold text-slate-700">Totale</th>
                    <th className="w-16 p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map(invoice => {
                    const actions = getAvailableActions(invoice.status);
                    return (
                    <tr
                      key={invoice.id}
                      className="border-b table-row-hover"
                      onClick={() => navigate(`/app/invoices/${invoice.id}`)}
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="p-4">
                        <div className="font-mono font-medium text-slate-900">{invoice.number}</div>
                      </td>
                      <td className="p-4 text-slate-700 font-medium">
                        {customers[invoice.customer_id]?.name_or_company || 'Cliente'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`status-badge ${getStatusColor(invoice.status)}`}>
                            {getStatusLabel(invoice.status)}
                          </span>
                          {invoice.status === 'OVERDUE' && invoice.days_overdue && (
                            <span className="text-xs text-red-600 font-medium">
                              da {invoice.days_overdue} giorni
                            </span>
                          )}
                          {/* Sending status icons */}
                          {(invoice.status === 'ISSUED' || invoice.status === 'OVERDUE' || invoice.status === 'SETTLED') && (() => {
                            const customer = customers[invoice.customer_id];
                            const deliveryMethod = customer?.invoice_delivery_method;
                            const showEmail = deliveryMethod === 'EMAIL' || deliveryMethod === 'BOTH';
                            const showMail = deliveryMethod === 'PAPER' || deliveryMethod === 'BOTH';
                            return (
                              <span className="flex items-center gap-1.5 ml-1">
                                {showEmail && (
                                  <span title={invoice.email_sent_at ? `Email inviata ${formatDate(invoice.email_sent_at)}` : 'Email da inviare'}>
                                    <Mail size={14} className={invoice.email_sent_at ? 'text-green-500' : 'text-slate-300'} />
                                  </span>
                                )}
                                {showMail && (
                                  <span title={invoice.mail_sent_at ? `Posta inviata ${formatDate(invoice.mail_sent_at)}` : 'Posta da inviare'}>
                                    <FileText size={14} className={invoice.mail_sent_at ? 'text-green-500' : 'text-slate-300'} />
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">{formatDate(invoice.created_at)}</td>
                      <td className="p-4 text-right font-mono">{formatCurrency(invoice.gross_total)}</td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        {actions.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                disabled={actionLoading === invoice.id}
                              >
                                <MoreHorizontal size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {actions.includes('settle') && (
                                <DropdownMenuItem onClick={() => handleQuickAction(invoice.id, 'settle')}>
                                  <DollarSign size={14} className="mr-2" />
                                  Salda
                                </DropdownMenuItem>
                              )}
                              {actions.includes('cancel') && (
                                <DropdownMenuItem 
                                  onClick={() => handleQuickAction(invoice.id, 'cancel')}
                                  className="text-red-600"
                                >
                                  <XCircle size={14} className="mr-2" />
                                  Annulla
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
